import { CreateApolloClient } from '@/api/client/apollo';
import { capture } from '@/handlers/error';
import { useSchool, useSession } from '@/providers/session/SessionProvider';
import { GoogleCourse, GoogleStudent } from '@/types/student';
import { fetchCourses, fetchStudents } from '@/utils/google/classroom';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    AccordionDetails,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    styled,
    Typography,
} from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { accordionSummaryClasses, AccordionSummaryProps } from '@mui/material/AccordionSummary';
import { Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { CREATE_CLASS, CREATE_STUDENTS, CreateClassResponse, CreateStudentsVariables, GET_CLASSES, GetClassesResponse } from './gql';

const PAGE_SIZE = 12;

function generatePassword(length: number = 12): string {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+~`|}{[]:;?><,./-=';

    // Combine all allowed characters
    const allChars = uppercase + lowercase + numbers + symbols;
    let password = '';

    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * allChars.length);
        password += allChars[randomIndex];
    }

    return password;
}

function Loader() {
    return <CircularProgress size={24} sx={{ display: 'block', m: 'auto', my: 1 }} />;
}

interface GoogleClassroomImportProgressRef {
    addLog: (log: string) => void;
}

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&::before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />} {...props} />)(({ theme }) => ({
    flexDirection: 'row-reverse',
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
        transform: 'rotate(90deg)',
    },
    [`& .${accordionSummaryClasses.content}`]: {
        marginLeft: theme.spacing(1),
    },
}));

function GoogleClassroomImportProgress(props: { show?: boolean; progress?: number; total?: number; ref: Ref<GoogleClassroomImportProgressRef> }) {
    const { t } = useTranslation();
    const { progress, total } = props;

    const isDone = total !== undefined && progress !== undefined && total >= 0 && progress >= total;

    const [logs, setLogs] = useState<string[]>([]);

    useImperativeHandle(
        props.ref,
        () => ({
            addLog: (log: string) => {
                setLogs((prev) => [...prev, log]);
            },
        }),
        [],
    );

    const handleDownloadLogs = useCallback(() => {
        const blob = new Blob([logs.join('\n')], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = 'import-google-classroom-logs.txt';

        link.click();

        URL.revokeObjectURL(url);
    }, [logs]);

    return (
        <Box sx={{ px: 2, py: 1, display: props.show ? undefined : 'none' }}>
            <Box>
                <Accordion sx={{ border: 0, backgroundColor: 'transparent', padding: 0 }}>
                    <AccordionSummary sx={{ pl: 1, pr: 0, minHeight: 36 }} slotProps={{ content: { sx: { my: 0 } } }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{isDone ? t('common.done') : t('student.import.description')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, position: 'relative' }}>
                        <Card variant='outlined' sx={{ p: 1.5, borderRadius: 0.5, overflow: 'auto', maxHeight: 400 }}>
                            <CardContent>
                                {logs.map((log, index) => (
                                    <Typography key={index} variant='caption' component='p' sx={{ whiteSpace: 'nowrap' }}>
                                        {log}
                                    </Typography>
                                ))}
                            </CardContent>
                        </Card>
                        {/* + 8 top for AccordionDetails top padding */}
                        <IconButton sx={{ position: 'absolute', top: 4 + 8, right: 4 }} onClick={handleDownloadLogs}>
                            <FileDownloadIcon />
                        </IconButton>
                    </AccordionDetails>
                </Accordion>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress variant='determinate' color='primary' value={total && progress ? (progress / total) * 100 : 0} />
                </Box>
                <Box>
                    <Typography>
                        {progress ?? 0}/{total ?? 0}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

function GoogleClassroomCourseList(props: { accessToken: string; onSelectedChange?: (courses: GoogleCourse[]) => void }) {
    const { t } = useTranslation();
    const { accessToken } = props;
    const [courses, setCourses] = useState<GoogleCourse[] | undefined>(undefined);
    const [selectedCourses, setSelectedCourses] = useState<GoogleCourse[]>([]);
    const [nextPageToken, setNextPageToken] = useState<string | undefined>(undefined);

    const [error, setError] = useState<string | undefined>(undefined);

    const fetchMoreCourses = async () => {
        await fetchCourses(accessToken, PAGE_SIZE, nextPageToken)
            .then((data) => {
                setNextPageToken(data.nextPageToken);
                setCourses((prev) => [...(prev || []), ...(data.courses || [])]);
            })
            .catch((error) => {
                capture(error);
                setError(error instanceof Error ? error.message : `${error}`);
            });
    };

    useEffect(() => {
        props.onSelectedChange?.(selectedCourses);
    }, [props, selectedCourses]);

    return (
        <InfiniteScroll dataLength={courses?.length || 0} next={fetchMoreCourses} hasMore={error ? false : courses === undefined || !!nextPageToken} loader={<Loader />}>
            {error ? (
                <Typography color='error' align='center' sx={{ my: 2 }}>
                    {error}
                </Typography>
            ) : (
                <List sx={{ p: 0 }}>
                    {courses?.length === 0 ? (
                        <Typography variant='body2' color='text.secondary' align='center' sx={{ my: 2 }}>
                            {t('student.import.noClassesFound')}
                        </Typography>
                    ) : (
                        courses?.map((course) => (
                            <ListItem key={course.id} disablePadding sx={{ '& .MuiButtonBase-root': { opacity: 1 } }}>
                                <ListItemButton
                                    onClick={() => {
                                        setSelectedCourses((prev) => {
                                            if (prev.some((c) => c.id === course.id)) {
                                                return prev.filter((c) => c.id !== course.id);
                                            }
                                            return [...prev, course];
                                        });
                                    }}
                                    disableRipple
                                    dense
                                >
                                    <ListItemText primary={course.name} secondary={course.section} />
                                    <ListItemIcon color='primary'>
                                        <Checkbox edge='end' checked={selectedCourses.some((c) => c.id === course.id)} sx={{ opacity: 1 }} />
                                    </ListItemIcon>
                                </ListItemButton>
                            </ListItem>
                        ))
                    )}
                </List>
            )}
        </InfiniteScroll>
    );
}

type StudentImportGoogleClassroomDialogProps = DialogProps & { accessToken: string; onClose?: () => void };

/**
 * Putting everything in term of Google Classroom logic into this dialog as a standalone component.
 * I don't see much opportunity to reuse the logic in other places for now.
 */
export function StudentImportGoogleClassroomDialog(props: StudentImportGoogleClassroomDialogProps) {
    const { t } = useTranslation();
    const { session } = useSession();
    const { school } = useSchool();

    const { onClose, open, accessToken, ...other } = props;

    // ui state
    const [selectedCourses, setSelectedCourses] = useState<GoogleCourse[]>([]);
    const [state, setState] = useState<'selecting' | 'preping' | 'importing' | 'done'>('selecting');
    const [total, setTotal] = useState<number | undefined>(undefined);
    const [progress, setProgress] = useState<number | undefined>(undefined);
    const abortControllerRef = useRef<AbortController | null>(null);
    const importProgressRef = useRef<GoogleClassroomImportProgressRef>(null);

    const handleCancel = () => {
        // cancel any unresolved request
        if (abortControllerRef.current) {
            importProgressRef.current?.addLog('Cancelling...');
            abortControllerRef.current.abort();
        }

        onClose?.();
    };

    const handleImport = useCallback(async () => {
        // cancel any previous unresolved request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        if (!school) {
            return;
        }

        setState('preping');

        importProgressRef.current?.addLog(`Import started`);

        const client = CreateApolloClient(session?.token);

        // classId -> students mapping
        const studentsToImport: Record<string, GoogleStudent[]> = {};

        // fetch all students of each selected course
        importProgressRef.current?.addLog(`Fetching ${selectedCourses.length} courses...`);
        for (const course of selectedCourses) {
            const classesResp = await client.query<GetClassesResponse>({
                query: GET_CLASSES,
                fetchPolicy: 'network-only',
                variables: {
                    skip: 0,
                    take: 1,
                    where: {
                        className: {
                            equals: course.name,
                        },
                    },
                },
            });

            if (abortControllerRef.current.signal.aborted) {
                return;
            }

            let classId: string | undefined = undefined;
            if (classesResp.data.classes.length > 0) {
                classId = classesResp.data.classes[0]?.id;
                importProgressRef.current?.addLog(`Found existing class: ${course.name}`);
            } else {
                importProgressRef.current?.addLog(`Creating class for course: ${course.name}`);
                const currentYear = new Date().getFullYear();
                const createClassResp = await client
                    .mutate<CreateClassResponse>({
                        mutation: CREATE_CLASS,
                        variables: {
                            data: {
                                className: course.name,
                                schoolYear: `${currentYear}-${currentYear + 1}`,
                                school: {
                                    connect: {
                                        id: school.id,
                                    },
                                },
                            },
                        },
                    })
                    .catch((error) => {
                        capture(error);
                    });
                classId = createClassResp?.data?.createClass?.id;
            }

            if (!classId) {
                continue;
            }

            if (abortControllerRef.current.signal.aborted) {
                return;
            }

            const studentsResp = await fetchStudents(accessToken, course.id);
            studentsToImport[classId] = studentsResp.students || [];
            importProgressRef.current?.addLog(`Fetched ${studentsResp.students?.length || 0} students for course: ${course.name}`);
            setTotal((prev) => (prev ?? 0) + (studentsResp.students?.length || 0));
        }

        // import
        setState('importing');
        setProgress(0);
        importProgressRef.current?.addLog(`Start importing ${Object.values(studentsToImport).flat().length} students...`);
        for (const [classId, students] of Object.entries(studentsToImport)) {
            for (const student of students) {
                if (abortControllerRef.current.signal.aborted) {
                    return;
                }

                /**
                 * We don't care if it is successfully imported or not, just try our best and move on.
                 * Even if there is account conflict we don't do anything special.
                 */
                const result = await client.mutate<unknown, CreateStudentsVariables>({
                    mutation: CREATE_STUDENTS,
                    variables: {
                        userCreateInputs: [
                            {
                                email: student.profile.emailAddress,
                                password: generatePassword(),
                                role: 'student',
                                name: student.profile.name.fullName,
                                studentProfile: {
                                    create: {
                                        familyName: student.profile.name.familyName,
                                        givenName: student.profile.name.givenName,
                                        class: {
                                            connect: {
                                                id: classId,
                                            },
                                        },
                                    },
                                },
                            },
                        ],
                    },
                    errorPolicy: 'all',
                });

                if (result.errors && result.errors.length > 0) {
                    const error = result.errors[0];

                    importProgressRef.current?.addLog(`Failed to import student ${student.profile.emailAddress}: ${error?.message}`);
                } else {
                    importProgressRef.current?.addLog(`Imported student ${student.profile.emailAddress} successfully`);
                }

                setProgress((prev) => (prev ?? 0) + 1);
            }
        }

        // done
        setState('done');
        importProgressRef.current?.addLog('Import completed');
    }, [accessToken, school, selectedCourses, session?.token]);

    return (
        // Make sure PAGE_SIZE of one page has a higher overall height than maxHeight,
        // otherwise it will not trigger the infinite scroll when reaching the bottom of scroll view
        <Dialog maxWidth='sm' fullWidth slotProps={{ paper: { sx: { maxHeight: 600 } } }} open={open} {...other}>
            <DialogTitle>Google Classroom</DialogTitle>
            <DialogContent dividers sx={{ px: 0, py: 1 }}>
                {state === 'selecting' && <GoogleClassroomCourseList accessToken={accessToken} onSelectedChange={setSelectedCourses} />}
                <GoogleClassroomImportProgress show={state !== 'selecting'} ref={importProgressRef} progress={progress} total={total} />
            </DialogContent>
            <DialogActions>
                {state === 'done' ? (
                    <Button onClick={handleCancel}>{t('common.close')}</Button>
                ) : (
                    <>
                        <Button onClick={handleCancel}>{t('common.cancel')}</Button>
                        <Button variant='contained' onClick={handleImport} loading={state === 'preping'} disabled={state === 'preping' || state === 'importing' || selectedCourses.length <= 0}>
                            {t('common.import')}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
